import React from "react";
import { PlusCircle } from "app/components/Icon";
import { EnterCoupon } from "app/components/Checkout/EnterCoupon";
import { PeriodUnitAdverbs } from "constants/cart";
import { BillingPeriodUnit } from "services/graphql";
import { formatCentsAsCurrency } from "helpers/currency";
import { P1 } from "app/components/Typography";
import { useCheckoutEstimate } from "hooks/Subscriptions/useCheckoutEstimate";
import { ToggleIconText } from "app/components/Checkout/ToggleIconText";
import styles from "./styles.module.scss";
import { BenefitsText } from "../BenefitsText";

interface Props {
  headerText: string;
  setIsSelectingPlan(isSelectingPlan: boolean): void;
  initialShowCoupon?: boolean;
}

export function CheckoutHeader({
  headerText,
  setIsSelectingPlan,
  initialShowCoupon,
}: Props) {
  const {
    selectedPlan,
    checkoutEstimate,
    isIntroOfferApplied,
  } = useCheckoutEstimate();

  const {
    canTrial,
    periodUnit,
    priceInCents,
    monthlyCostInCents,
    currencyCode,
  } = selectedPlan;

  function generateCopy() {
    let subHeaderText;
    let breakdownText;
    let amountDueToday;

    const planPrice = (
      (checkoutEstimate?.discountPlanPriceInCents ?? priceInCents ?? 0) / 100
    ).toFixed(2);

    const monthlyCents =
      checkoutEstimate?.monthlyDiscountPlanPriceInCents ??
      monthlyCostInCents ??
      0;

    if (isIntroOfferApplied) {
      subHeaderText = formatCentsAsCurrency(0, currencyCode);
      amountDueToday = planPrice;
      breakdownText = (
        <>
          After one year, you&apos;ll be automatically enrolled in our{" "}
          <strong>
            {formatCentsAsCurrency(priceInCents ?? 0, currencyCode)} + tax/
            {periodUnit} plan.{" "}
          </strong>
        </>
      );
    } else if (canTrial) {
      subHeaderText = formatCentsAsCurrency(0, currencyCode);
      amountDueToday = "0.00";

      breakdownText = (
        <>
          After your free trial, you&apos;ll be automatically enrolled in our{" "}
          <strong>
            {formatCentsAsCurrency(priceInCents ?? 0, currencyCode)} + tax/
            {periodUnit} plan.{" "}
          </strong>
          Change or cancel anytime during your trial to not be charged.
        </>
      );
    } else if (periodUnit === BillingPeriodUnit.Year) {
      amountDueToday = planPrice;
      subHeaderText = (
        <>
          Billed annually at{" "}
          {formatCentsAsCurrency(priceInCents ?? 0, currencyCode)}
        </>
      );
      breakdownText = (
        <>
          That&apos;s just {formatCentsAsCurrency(monthlyCents, currencyCode)}
          /MO
        </>
      );
    } else {
      amountDueToday = planPrice;
      subHeaderText = (
        <>
          Billed monthly at{" "}
          {formatCentsAsCurrency(priceInCents ?? 0, currencyCode)}
        </>
      );
      breakdownText = null;
    }

    return { subHeaderText, breakdownText, amountDueToday };
  }

  const { breakdownText, amountDueToday } = generateCopy();

  return (
    <div>
      <div className={styles.selectedPlanHeader}>
        <header>{selectedPlan.accessType}</header>
        {
          PeriodUnitAdverbs[
            selectedPlan.periodUnit as keyof typeof PeriodUnitAdverbs
          ]
        }
      </div>
      <div className={styles.headerContainer}>
        <h2 className={styles.headerText}>{headerText}</h2>
        <BenefitsText
          canTrial={selectedPlan.canTrial && !isIntroOfferApplied}
        />
        <div>
          {!isIntroOfferApplied && (
            <ToggleIconText
              icon={PlusCircle}
              text="Switch Plans"
              onClick={() => setIsSelectingPlan(true)}
            />
          )}

          <EnterCoupon initialShowCoupon={initialShowCoupon} />
        </div>
        <div className={styles.dividerThin} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <P1>Amount due today:</P1>
          <P1 className={styles.amountDueTotal}>
            {formatCentsAsCurrency(Number(amountDueToday) * 100, currencyCode)}
          </P1>
        </div>

        <p className={styles.breakdownText}>{breakdownText}</p>
      </div>
    </div>
  );
}
